import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { ApiError, api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

/**
 * Confirmation d'une déconnexion demandée SANS `id_token_hint`.
 *
 * ── Pourquoi cet écran existe ──────────────────────────────────────────────
 *
 * `GET /oidc/logout` fermait la session SSO sur simple requête : aucun indice,
 * aucune confirmation, aucune vérification d'origine. Un lien posé sur
 * n'importe quel site déconnectait donc tout visiteur de la suite — les trois
 * produits et les trois back-offices d'un coup.
 *
 * La spécification RP-Initiated Logout prévoit deux issues : ou bien
 * l'application présente un `id_token_hint` signé par ce portail, et la
 * déconnexion se fait sans rien demander ; ou bien on demande à la personne.
 * Refuser purement et simplement aurait cassé les déconnexions légitimes des
 * clients qui ne transmettent pas encore l'indice — cet écran les accueille,
 * en une question.
 *
 * Même raisonnement que la récupération MFA, un écran plus loin : une action
 * irréversible déclenchée par un GET finit toujours par être exécutée par
 * quelqu'un qui ne l'a pas voulue.
 */
/**
 * URL de retour après déconnexion : http(s) seulement (jamais `javascript:`),
 * `https` hors du poste de développement, avec le `state` du client renvoyé
 * comme le prévoit la déconnexion initiée par le client (RP-Initiated Logout).
 */
function retourSur(brut: string | null, state: string | null): string | null {
  if (!brut) return null;
  try {
    const url = new URL(brut);
    const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (url.protocol !== 'https:' && !(local && url.protocol === 'http:')) return null;
    if (state) url.searchParams.set('state', state);
    return url.toString();
  } catch {
    return null;
  }
}

export function LogoutPage() {
  const [searchParams] = useSearchParams();
  const retour = retourSur(searchParams.get('post_logout_redirect_uri'), searchParams.get('state'));
  const [etat, setEtat] = useState<'question' | 'en-cours' | 'fait' | 'erreur'>('question');
  const setUser = useAuthStore((state) => state.setUser);

  async function confirmer() {
    setEtat('en-cours');
    try {
      await api.post('/auth/logout');
      setUser(null);
      if (retour) {
        // L'API ne relaie que les URI enregistrées (01/11/2026) ; le portail
        // refuse en plus tout ce qui n'est pas http(s) — on peut ouvrir
        // `/deconnexion` directement avec n'importe quel paramètre.
        window.location.assign(retour);
        return;
      }
      setEtat('fait');
    } catch (err) {
      // Pas de session à fermer : le but est atteint, pas manqué. Répondre
      // « erreur » ferait douter quelqu'un déjà déconnecté.
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        if (retour) {
          window.location.assign(retour);
          return;
        }
        setEtat('fait');
        return;
      }
      setEtat('erreur');
    }
  }

  if (etat === 'fait') {
    return (
      <AuthCard title="Vous êtes déconnecté">
        <Alert message="Votre session SEmply a été fermée sur tous les outils." variant="success" />
        <Link className="btn" to="/login">
          Se reconnecter
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Se déconnecter de SEmply ?"
      subtitle="Cette déconnexion vaut pour tous les outils de la suite, sur cet appareil."
    >
      {etat === 'erreur' ? (
        <Alert message="La déconnexion n'a pas abouti. Réessayez dans un instant." />
      ) : null}
      <button
        className="btn"
        type="button"
        disabled={etat === 'en-cours'}
        onClick={() => void confirmer()}
      >
        {etat === 'en-cours' ? 'Déconnexion…' : 'Oui, me déconnecter'}
      </button>
      <Link className="btn btn-secondary" style={{ marginTop: '0.5rem' }} to="/produits">
        Rester connecté
      </Link>
    </AuthCard>
  );
}
